/**
 * The `/api/log` ingest boundary's published limits on `LogError`.
 *
 * The ingest route is unauthenticated, so its Zod schema *is* the trust
 * boundary and stays strict: an entry that violates it is dropped whole and
 * the caller still gets a 2xx (see ingest.ts — drop what does not parse,
 * forward the rest, always answer 2xx). Nothing on the server may repair an
 * over-long value, because repairing it means mutating untrusted input to
 * make it pass the very check it just failed.
 *
 * That leaves the client as the only correct place to keep a real event
 * from turning into a silent drop, and the client needs the same numbers
 * the schema enforces. They live here, in the environment-agnostic package
 * both sides can import (a Worker route may depend on `@bindery/logging`;
 * the reverse is not true), rather than as literals duplicated across the
 * schema and the clamp. `toLogError` clamps to these; the
 * route's schema enforces them; neither owns a copy.
 *
 * Denominated in UTF-16 code units — JS `.length` — because that is what
 * `z.string().max()` counts, so a `.length` clamp on the client lands
 * exactly on the boundary rather than near it. Verified against the
 * zod 4.4.3 this repo resolves: `z.string().max(4)` accepts `"中".repeat(4)`
 * (`.length` 4, 12 UTF-8 bytes) and rejects `"abcde"`.
 *
 * The route's *other* caps — the `data` bag and the whole POST body — are
 * byte-denominated and deliberately not published here. A client cannot
 * clamp a structured bag without deciding which of a call site's keys to
 * sacrifice, so those stay local to ingest.ts as pure boundary checks.
 *
 * The keepalive budget at the bottom of this file is the one byte-denominated
 * number that does belong here, and for the same reason the error caps do:
 * it is a limit the *client* has to respect, imposed by the Fetch spec rather
 * than by the route, and the transport cannot import ingest.ts to find it
 * without pulling zod into the browser bundle. `byteLength` moved here with
 * it so both sides measure bytes the same way.
 */

/**
 * `domain.action` shape (policy §2): at least one dot, lowercase
 * segments/underscores only. Deliberately not pinned to a runtime domain
 * list, so adding a domain does not require a matching ingest change.
 */
export const EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/;

export const MAX_EVENT_NAME_LENGTH = 200;

/** Cap on `LogError.type` (the error's `name`). */
export const MAX_ERROR_TYPE_LENGTH = 256;

/**
 * Cap on `LogError.message`. The value most likely to blow a cap:
 * `new Error("request failed: " + (await res.text()))` against an HTML
 * error page clears 4096 characters without trying.
 */
export const MAX_ERROR_MESSAGE_LENGTH = 4096;

/** Cap on `LogError.code`. */
export const MAX_ERROR_CODE_LENGTH = 64;

const textEncoder = new TextEncoder();

/**
 * The actual UTF-8 byte length of a string, not its JS `.length` (UTF-16
 * code units). A string full of emoji or CJK text can have a `.length` well
 * under its real serialized size, so comparing `.length` against a
 * byte-denominated cap understates size and can pass a payload the cap was
 * meant to reject.
 *
 * Lives here rather than in ingest.ts so `WorkerLogBackend` can measure a
 * request body without importing the schema module; ingest.ts re-exports it,
 * because that is where the caps it serves are declared.
 */
export function byteLength(s: string): number {
  return textEncoder.encode(s).length;
}

/**
 * The Fetch spec's keepalive quota: 64 KiB, summed across *all* in-flight
 * keepalive requests in a document, and a request that would push the sum
 * past it fails with a network error rather than queueing behind the others.
 *
 * Not a number this repo chose, and not one it can raise. It is here so the
 * budget below is visibly derived from it rather than asserted.
 *
 * Note "all in-flight requests in a document": `WorkerLogBackend` can only
 * account for its own, so anything else on the page issuing keepalive fetches
 * (or `navigator.sendBeacon`, which draws on the same pool) spends quota this
 * package never sees. That is another reason the per-event budget below is
 * sized well under a strict 64 KiB / burst-depth split.
 */
export const KEEPALIVE_QUOTA_BYTES = 64 * 1024;

/**
 * The largest serialized request body `WorkerLogBackend` will spend keepalive
 * quota on.
 *
 * Sized from the caps above rather than picked: the biggest event the ingest
 * schema accepts *outside* its `data` bag is roughly
 * `MAX_ERROR_MESSAGE_LENGTH` + `MAX_ERROR_TYPE_LENGTH` +
 * `MAX_ERROR_CODE_LENGTH` + a maximal `eventName` and the correlation ids —
 * about 5.5 KiB, and `error.message` at its full 4096 characters is a real
 * shape (`new Error("request failed: " + await res.text())` against an HTML
 * error page). A budget under that would quietly strip keepalive from exactly
 * the unhandled-error events most worth surviving unload, so 8 KiB is the
 * next power of two up, leaving ~2.5 KiB of headroom for `data`.
 *
 * Deliberately *below* ingest.ts's 16 KiB `MAX_LOG_DATA_BYTES`. The two are
 * answering different questions and are supposed to differ: the route must
 * accept a legitimately large bag it has no way to shrink, while the browser
 * gets to decide that a bag that large is not worth a quarter of the
 * document's unload quota. An event over this budget is still sent — it just
 * goes without `keepalive`, which costs it only the unload case, instead of
 * risking outright rejection and starving the small events around it.
 */
export const MAX_KEEPALIVE_EVENT_BYTES = 8 * 1024;

/**
 * How many maximum-size events can be in flight at once inside the quota —
 * the burst depth the budget above buys, stated as a number so a test can
 * check the arithmetic rather than trusting the comment.
 *
 * Eight, against a real page-hide burst of roughly six: LCP, INP and CLS
 * flushing together, `PerformanceTelemetry`'s `visibility_hidden` long-task
 * chunk, and a Compute Pressure transition or two. Measured, not guessed —
 * that exact burst in worker.test.ts serializes to ~350 bytes an event and
 * a little over 2 KiB in total, some 3% of the quota, so the practical depth
 * is in the hundreds. Eight is the floor that holds even in the case that
 * cannot happen by accident: every event in the burst arriving at its cap.
 */
export const KEEPALIVE_BURST_DEPTH =
  KEEPALIVE_QUOTA_BYTES / MAX_KEEPALIVE_EVENT_BYTES;
